import type { DocumentUploadStatus } from 'commonTypes/api/scenarios/DocumentsLoading';

import { EVENT_CATEGORIES, EVENT_TYPES } from '../constants';
import type { IInitialEvent, TAnalyticsNumericRange } from '../types';

// События экранов MPL

const EVENT_CATEGORY = EVENT_CATEGORIES.MARKETPLACE_LOANS;
const EVENT_TYPE = EVENT_TYPES.MAIN_EVENT;

export const eventOnShowMplOffersLoadingScreenStep = (scenario: string, loadingStep: number): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Показ',
  eventLabel: `${scenario} | Шаг ${loadingStep}`,
  eventValue: undefined,
});

export const eventOnRatingDescriptionClickOnMplOffersLoadingScreen = (scenario: string): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Клик',
  eventLabel: `${scenario} | Рейтинг`,
  eventValue: undefined,
});

export const eventOnShowRatingDescriptionPopupOnMplOffersLoadingScreen = (scenario: string): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Показ',
  eventLabel: `${scenario} | Какой бывает рейтинг`,
  eventValue: undefined,
});

export const eventOnShowPDNOnMplOffersLoadingScreen = (scenario: string): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Показ',
  eventLabel: `${scenario} | ПДН`,
  eventValue: undefined,
});

export const eventOnShowApprovedOffersPopupOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankApprovalsCount: number;
  notAnsweredBanksCount?: number;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankApprovalsCount, notAnsweredBanksCount } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      `Одобрений - ${bankApprovalsCount}`,
      notAnsweredBanksCount ? `Не все ответили - ${notAnsweredBanksCount}` : '',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnCloseApprovedOffersPopupOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankApprovalsCount: number;
  notAnsweredBanksCount?: number;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankApprovalsCount, notAnsweredBanksCount } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      `Одобрений - ${bankApprovalsCount}`,
      notAnsweredBanksCount ? `Не все ответили - ${notAnsweredBanksCount}` : '',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnHiddenSlidesLoader = (data: { loaderDurationInSeconds?: number }): IInitialEvent => {
  const { loaderDurationInSeconds } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: `Время загрузки - ${loaderDurationInSeconds}`,
    eventValue: undefined,
  };
};

export const eventOnExitMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  filter: string;
  searchOutput: number;
  subCategory: number;
  calcId: string | null;
  timeOnPage?: number;
}): IInitialEvent => {
  const { scenario, filter, category, subCategory, calcId, timeOnPage, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Выход',
    eventLabel: [
      scenario,
      searchOutput,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      `Время - ${timeOnPage}`,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnLeadGenFiltersChange = (data: {
  scenario: string;
  searchOutput: number;
  amount: string | number;
  period: string;
  purpose: string;
}): IInitialEvent => {
  const { scenario, searchOutput, amount, period, purpose } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      searchOutput,
      `Изменение суммы - ${amount}`,
      `Изменение срока - ${period}`,
      `Изменение цели - ${purpose}`,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnLeadGenOffersChipsChange = (data: {
  scenario: string;
  searchOutput: number;
  params: string;
}): IInitialEvent => {
  const { scenario, searchOutput, params } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, searchOutput, `Параметры кредита - ${params}`, `Новые параметры`].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickEsiaAuth = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, searchOutput, `Войти в Госуслуги`].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickAwaitingCall = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, searchOutput, `Вам позвонят`].join(' | '),
    eventValue: undefined,
  };
};

type TOfferCardAnalyticsEventData = {
  scenario: string;
  searchOutput: number;
  offerType: string;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string | null;
  productName: string;
  organizationId: string | number;
  organizationName: string;
  offerIndex: number;
  psk?: string;
  sum?: string;
  rate?: string;
  term?: string;
  badges: string;
  status: string;
};

const getOfferCardAnalyticsEventLabel = (data: TOfferCardAnalyticsEventData) => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    productName,
    organizationId,
    organizationName,
    offerIndex,
    psk,
    sum,
    rate,
    term,
    badges,
    status,
  } = data;

  return [
    scenario,
    searchOutput,
    offerType,
    filter,
    `Категория - ${category}`,
    `Подкатегория - ${subCategory}`,
    calcId,
    productId,
    offerIndex,
    organizationId,
    organizationName,
    productName,
    psk ? `ПСК - ${psk}` : '',
    sum ? `Сумма - ${sum}` : '',
    rate ? `Ставка - ${rate}` : '',
    term ? `Срок - ${term}` : '',
    badges,
    status,
  ].join(' | ');
};

export const eventOnShowOfferOnMplCreditSelectionScreen = (data: TOfferCardAnalyticsEventData): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Показ',
  eventLabel: getOfferCardAnalyticsEventLabel(data),
  eventValue: undefined,
});

export const eventOnClickMfoDealOfferOnMplCreditSelectionScreen = (
  data: TOfferCardAnalyticsEventData,
): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Клик',
  eventLabel: getOfferCardAnalyticsEventLabel(data),
  eventValue: undefined,
});

export const eventOnClickMfoDealContinueOnMplCreditSelectionScreen = (
  data: TOfferCardAnalyticsEventData,
): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Клик_ПС_МФО',
  eventLabel: getOfferCardAnalyticsEventLabel(data),
  eventValue: undefined,
});

export const eventOnClickOfferOnMplCreditSelectionScreen = (
  data: {
    scenario: string;
    searchOutput: number;
    offerType: string;
    filter: string;
    category: number;
    subCategory: number;
    calcId: string | null;
    productName: string;
    organizationId: string | number;
    productType: string;
    organizationName: string;
    offerIndex: number;
    psk: string;
    sum: string;
    rate: string;
    term: string;
    badges: string;
    timeOnPage: number;
  },
  eventAction: 'Клик_MPL_Предложение' | 'Клик_лидген_предложение',
): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productName,
    organizationId,
    productType,
    organizationName,
    offerIndex,
    psk,
    sum,
    rate,
    term,
    badges,
    timeOnPage,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction,
    eventLabel: [
      scenario,
      searchOutput,
      offerType,
      filter,
      `Тип продукта - ${productType}`,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productName,
      offerIndex,
      organizationId,
      organizationName,
      psk ? `ПСК - ${psk}` : '',
      sum ? `Сумма - ${sum}` : '',
      rate ? `Ставка - ${rate}` : '',
      term ? `Срок - ${term}` : '',
      badges,
      `Время - ${timeOnPage}`,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowAwaitingCallOffer = (data: {
  scenario: string;
  searchOutput: number;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
  psk: string;
  sum: string;
  rate: string;
  term: string;
  badges: string;
  status: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
    psk,
    sum,
    rate,
    term,
    badges,
    status,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      searchOutput,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      psk ? `ПСК - ${psk}` : '',
      sum ? `Сумма - ${sum}` : '',
      rate ? `Ставка - ${rate}` : '',
      term ? `Срок - ${term}` : '',
      badges,
      'Вам позвонят',
      status,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowAwaitingCallOfferDetailsPopup = (data: {
  scenario: string;
  searchOutput: number;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
  psk: string;
  sum: string;
  rate: string;
  term: string;
  badges: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
    psk,
    sum,
    rate,
    term,
    badges,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      searchOutput,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      psk ? `ПСК - ${psk}` : '',
      sum ? `Сумма - ${sum}` : '',
      rate ? `Ставка - ${rate}` : '',
      term ? `Срок - ${term}` : '',
      badges,
      'Вам позвонят',
      'Детали предложения',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowBankRefusedBanner = (data: {
  scenario: string;
  searchOutput: number;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
}): IInitialEvent => {
  const { scenario, searchOutput, filter, category, subCategory, calcId } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      searchOutput,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      'Отказ на рескоринге',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowApplicationsStatusModal = (data: {
  scenario: string;
  searchOutput: number;
  modalTitle: string;
  offerType?: string;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId?: string | number | null;
  organizationId?: string | number | null;
  organizationName?: string;
  status?: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    modalTitle,
    offerType = '',
    filter,
    category,
    subCategory,
    calcId,
    productId = '',
    organizationId = '',
    organizationName = '',
    status = '',
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      searchOutput,
      modalTitle,
      offerType,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      status,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnChangeMplPreApprovalDeliveryFormField = (data: {
  scenario: string;
  searchOutput: number;
  offerType: string;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
  address: string;
  date: string;
  time: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
    address,
    date,
    time,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      searchOutput,
      offerType,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      `Адрес встречи - ${address}`,
      `Дата - ${date}`,
      `Время - ${time}`,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSubmitMplPreApprovalDeliveryFormField = (data: {
  scenario: string;
  searchOutput: number;
  filter: string;
  offerType: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
  address: string;
  date: string;
  time: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
    address,
    date,
    time,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      searchOutput,
      offerType,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      `Адрес встречи - ${address}`,
      `Дата - ${date}`,
      `Время - ${time}`,
      'Подтвердить',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowMplPreApprovalSmsConfirmation = (data: {
  scenario: string;
  searchOutput: number;
  offerType: string;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      searchOutput,
      offerType,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      'Ввод смс кода',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnFailedMplPreApprovalSmsConfirmation = (data: {
  scenario: string;
  searchOutput: number;
  offerType: string;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      searchOutput,
      offerType,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      'Ввод смс кода',
      'Неверный код смс',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnRequestNewSmsCodeForPreApproval = (data: {
  scenario: string;
  searchOutput: number;
  offerType: string;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
  } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      searchOutput,
      offerType,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      'Ввод смс кода',
      'Запрос нового кода смс',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnCorrectSmsCodeForPreApproval = (data: {
  scenario: string;
  searchOutput: number;
  offerType: string;
  filter: string;
  category: number;
  subCategory: number;
  calcId: string | null;
  productId: string;
  organizationId: string | number;
  organizationName: string;
}): IInitialEvent => {
  const {
    scenario,
    searchOutput,
    offerType,
    filter,
    category,
    subCategory,
    calcId,
    productId,
    organizationId,
    organizationName,
  } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      searchOutput,
      offerType,
      filter,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      productId,
      organizationId,
      organizationName,
      'Ввод смс кода',
      'Верный код смс',
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickWhyThisAmountOnMplCreditSelectionScreen = (scenario: string): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Клик',
  eventLabel: `${scenario} | Почему такая сумма`,
  eventValue: undefined,
});

export const eventOnClickChangeAmountOnMplCreditSelectionScreen = (
  scenario: string,
  amount: number,
): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Клик',
  eventLabel: `${scenario} | ${amount} | Изменить`,
  eventValue: undefined,
});

export const eventOnClickApprovalDateOnMplCreditSelectionScreen = (
  scenario: string,
  amount: number,
): IInitialEvent => ({
  event: EVENT_TYPE,
  eventCategory: EVENT_CATEGORY,
  eventAction: 'Клик',
  eventLabel: `${scenario} | ${amount} | До какого числа`,
  eventValue: undefined,
});

export const eventOnShowTopXOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  banksCount: number;
  banksNames: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, banksCount, banksNames } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      'Топ Х',
      `Кол-во банков - ${banksCount}`,
      banksNames,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSubmitClickTopXOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  banksCount: number;
  banksNames: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, banksCount, banksNames } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик_topX',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      'Топ Х',
      `Кол-во банков - ${banksCount}`,
      banksNames,
    ].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowRefusedOffersOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  calcId?: string;
  refusedProductIds: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, calcId = '', refusedProductIds = '' } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      calcId,
      refusedProductIds,
      'Почему отказали',
    ].join(' | '),
    eventValue: undefined,
  };
};

// TODO определить, где именно вызвать этот эвент
export const eventOnClickWhyRefusedOffersOnMplCreditSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
}): IInitialEvent => {
  const { scenario, category, subCategory } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, 'Почему отказали'].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowConditionsSelectionScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  productId?: string;
  organizationId?: number;
  bankName?: string;
  paymentAmount?: number;
  psk?: number | string;
  amount?: number;
  rate?: number;
  term?: string;
  isWithInsurance?: boolean;
  closingDate?: string;
}): IInitialEvent => {
  const {
    scenario,
    category,
    subCategory,
    productId,
    organizationId,
    bankName,
    paymentAmount,
    psk,
    amount,
    rate,
    term,
    isWithInsurance,
    closingDate,
  } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      productId,
      organizationId,
      bankName,
      `Платеж - ${paymentAmount}`,
      `ПСК - ${psk}`,
      `Сумма - ${amount}`,
      `Ставка - ${rate}`,
      `Срок - ${term}`,
      isWithInsurance ? 'Финзащита' : 'без фин. защиты',
      `Дата закрытия - ${closingDate}`,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickTermUnavailableReason = (data: {
  scenario: string;
  category: number;
  subCategory: number;
}): IInitialEvent => {
  const { scenario, category, subCategory } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, 'Почему не все сроки']
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickChangeOption = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName: string;
  paymentAmount: number | string;
  psk: number | string;
  amount: number | string;
  rate: number | string;
  term: string;
  isWithInsurance?: boolean;
  closingDate: string;
}): IInitialEvent => {
  const {
    scenario,
    category,
    subCategory,
    bankName,
    paymentAmount,
    psk,
    amount,
    rate,
    term,
    isWithInsurance,
    closingDate,
  } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Платеж - ${paymentAmount}`,
      `ПСК - ${psk}`,
      `Сумма - ${amount}`,
      `Ставка - ${rate}`,
      `Срок - ${term}`,
      isWithInsurance ? 'Финзащита' : 'без фин. защиты',
      `Дата закрытия - ${closingDate}`,
      'Выбор настроек',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickInsuranceDetails = (data: {
  scenario?: string;
  category?: number;
  subCategory?: number;
}): IInitialEvent => {
  const { scenario, category, subCategory } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, 'Финзащита']
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnOpenDocumentsList = (data: {
  scenario: string;
  category: number;
  subCategory: number;
}): IInitialEvent => {
  const { scenario, category, subCategory } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, 'Документы']
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnChangeAmount = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  amount: number;
}): IInitialEvent => {
  const { scenario, category, subCategory, amount } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, `Изменение суммы - ${amount}`]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnEditConditionsClickOnLoadingDocumentsScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  paymentAmount?: number;
  psk?: number;
  amount?: number;
  rate?: number;
  term?: string;
  isWithInsurance?: boolean;
  closingDate?: string;
}): IInitialEvent => {
  const {
    scenario,
    category,
    subCategory,
    bankName,
    paymentAmount,
    psk,
    amount,
    rate,
    term,
    isWithInsurance,
    closingDate,
  } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Платеж - ${paymentAmount}`,
      `ПСК - ${psk}`,
      `Сумма - ${amount}`,
      `Ставка - ${rate}`,
      `Срок - ${term}`,
      isWithInsurance ? 'Финзащита' : 'без фин. защиты',
      `Дата закрытия - ${closingDate}`,
      'Редактирование условий',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowLoadingDocumentsScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  documents?: Array<{ name: string; status: DocumentUploadStatus }>;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, documents } = data;
  const statusDescriptions: Record<DocumentUploadStatus, string> = {
    pending: 'Загружается',
    empty: 'Не загружен',
    verified: 'Проверен',
    rejected: 'Не прошел проверку',
  };
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Загрузка документов',
      documents?.map((doc) => `${doc.name} - ${statusDescriptions[doc.status]}`).join(', '),
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSelectDocumentInChoiceModal = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  // TODO у выбираемого документа нет status
  document: { name: string; status: DocumentUploadStatus };
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, document } = data;
  const statusDescriptions: Record<DocumentUploadStatus, string> = {
    pending: 'Загружается',
    empty: 'Не загружен',
    verified: 'Проверен',
    rejected: 'Не прошел проверку',
  };
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Загрузка документов',
      `${document.name} - ${statusDescriptions[document.status]}`,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowAgreementSigningScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  // TODO о каком статусе идет речь?
  agreementSigningStatus: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, agreementSigningStatus } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Подписание - ${agreementSigningStatus}`,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnDocumentClickOnAgreementSigningScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  // TODO о каком статусе идет речь?
  agreementSigningStatus: string;
  documentName: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, agreementSigningStatus, documentName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Подписание - ${agreementSigningStatus}`,
      documentName,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSubmitAgreementSigning = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  // TODO о каком статусе идет речь?
  agreementSigningStatus: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, agreementSigningStatus } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Подписание - ${agreementSigningStatus}`,
      'Подписываю',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSendSmsAgreementSigning = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  // TODO о каком статусе идет речь?
  agreementSigningStatus: string;
  smsCode: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, agreementSigningStatus, smsCode } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Подписание - ${agreementSigningStatus}`,
      'Подписал',
      `смс - ${smsCode}`,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSmsErrorAgreementSigning = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  // TODO о каком статусе идет речь?
  agreementSigningStatus: string;
  smsError: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, agreementSigningStatus, smsError } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Подписание - ${agreementSigningStatus}`,
      'Не пришла смс',
      `ошибка - ${smsError}`,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSendNewSmsAgreementSigning = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  // TODO о каком статусе идет речь?
  agreementSigningStatus: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, agreementSigningStatus } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Подписание - ${agreementSigningStatus}`,
      'Перезапросил СМС',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowOfferApprovalModal = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  oldConditions: string;
  newConditions: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, oldConditions, newConditions } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Условия изменились`,
      `Старые условия - ${oldConditions}`,
      `Новые условия - ${newConditions}`,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickButtonOffersOnOfferApprovalModal = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
  oldConditions: string;
  newConditions: string;
  isApproved: boolean;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName, oldConditions, newConditions, isApproved } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      `Условия изменились`,
      `Старые условия - ${oldConditions}`,
      `Новые условия - ${newConditions}`,
      isApproved ? 'Применить' : 'Перейти к другим предложениям',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowCoolingPeriodScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, bankName, 'Период охлаждения']
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowWhyNeedCoolingPeriodModal = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Период охлаждения',
      'Почему нужно ждать',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowAgreementSignedScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  // todo на этом экране нет данных по банку
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, bankName, 'Договор подписан']
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowOfferDisbursementScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, `Категория - ${category}`, `Подкатегория - ${subCategory}`, bankName, 'Счет открыт']
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickNotSendSmsOnOfferDisbursementScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Счет открыт',
      'Не пришла СМС',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowNotSendSmsModalOnOfferDisbursementScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Счет открыт',
      'Что делать если не пришла смс',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickRedirectToBankOnOfferDisbursementScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Счет открыт',
      'Перейти в онлайн банк',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickOpenDocumentOnOfferDisbursementScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Счет открыт',
      'Документы',
      'Cсылка на документ',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowClientRefusedScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  // todo в сценарии нет данных по банку
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Оформление недоступно',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickGoToMainOnClientRefusedScreen = (data: {
  scenario: string;
  category: number;
  subCategory: number;
  // todo в сценарии нет данных по банку
  bankName?: string;
}): IInitialEvent => {
  const { scenario, category, subCategory, bankName } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      'Оформление недоступно',
      'На главный экран',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickSelectOfferOnConditionsSelectionScreen = (data: {
  scenario?: string;
  category?: number;
  subCategory?: number;
  productId?: string;
  organizationId?: number;
  bankName?: string;
  tariffName?: string;
  paymentAmount?: number;
  psk?: number | string;
  amount?: number | TAnalyticsNumericRange;
  rate?: number | TAnalyticsNumericRange;
  term?: string;
  isWithInsurance?: boolean;
  closingDate?: string;
}): IInitialEvent => {
  const {
    scenario,
    category,
    subCategory,
    productId,
    organizationId,
    bankName,
    tariffName,
    paymentAmount,
    psk,
    amount,
    rate,
    term,
    isWithInsurance,
    closingDate,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      productId,
      organizationId,
      bankName,
      tariffName,
      `Платеж - ${paymentAmount}`,
      `ПСК - ${psk}`,
      `Сумма - ${amount}`,
      `Ставка - ${rate}`,
      `Срок - ${term}`,
      isWithInsurance ? 'Финзащита' : 'без фин. защиты',
      `Дата закрытия - ${closingDate}`,
      'Выбрать и продолжить',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickActiveOfferConflictOnConditionsSelectionScreen = (data: {
  scenario?: string;
  category?: number;
  subCategory?: number;
  bankName?: string;
  productId?: string;
  organizationId?: number;
  tariffName?: string;
  amount?: number | string;
  rate?: number | string;
  term?: string;
  newBankName?: string;
  newProductId?: string;
  newOrganizationId?: number;
  newTariffName?: string;
  newAmount?: number | string;
  newRate?: number | string;
  newTerm?: string;
}): IInitialEvent => {
  const {
    scenario,
    category,
    subCategory,
    bankName,
    productId,
    organizationId,
    tariffName,
    amount,
    rate,
    term,
    newBankName,
    newProductId,
    newOrganizationId,
    newTariffName,
    newAmount,
    newRate,
    newTerm,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      bankName,
      productId,
      organizationId,
      tariffName,
      `Сумма - ${amount}`,
      `Ставка - ${rate}`,
      `Срок - ${term}`,
      `Перевыбор оффера на - ${newBankName}`,
      newProductId,
      newOrganizationId,
      `Новый тариф - ${newTariffName}`,
      `Новая сумма - ${newAmount}`,
      `Новая ставка - ${newRate}`,
      `Новый срок - ${newTerm}`,
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnToggleInsuranceOnConditionsSelectionScreen = (data: {
  scenario?: string;
  category?: number;
  subCategory?: number;
  productId?: string;
  organizationId?: number;
  bankName?: string;
  tariffName?: string;
  paymentAmount?: number;
  psk?: number | string;
  amount?: number | TAnalyticsNumericRange;
  rate?: number | TAnalyticsNumericRange;
  term?: string;
  isWithInsurance?: boolean;
  closingDate?: string;
  isSwitchToInsurance?: boolean;
}): IInitialEvent => {
  const {
    scenario,
    category,
    subCategory,
    productId,
    organizationId,
    bankName,
    tariffName,
    paymentAmount,
    psk,
    amount,
    rate,
    term,
    isWithInsurance,
    closingDate,
    isSwitchToInsurance,
  } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      productId,
      organizationId,
      bankName,
      tariffName,
      `Платеж - ${paymentAmount}`,
      `ПСК - ${psk}`,
      `Сумма - ${amount}`,
      `Ставка - ${rate}`,
      `Срок - ${term}`,
      isWithInsurance ? 'Финзащита' : 'без фин. защиты',
      `Дата закрытия - ${closingDate}`,
      isSwitchToInsurance ? 'С финзащитой' : 'Без финзащиты',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickButtonForConsentUpdate = (data: {
  scenario: string;
  searchOutput: number;
  category: number;
  subCategory: number;
}): IInitialEvent => {
  const { scenario, searchOutput, category, subCategory } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [
      scenario,
      searchOutput,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      'Обновить согласия Сравни',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowButtonForConsentUpdate = (data: {
  scenario: string;
  searchOutput: number;
  category: number;
  subCategory: number;
}): IInitialEvent => {
  const { scenario, searchOutput, category, subCategory } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [
      scenario,
      searchOutput,
      `Категория - ${category}`,
      `Подкатегория - ${subCategory}`,
      'Обновить согласия Сравни',
    ]
      .filter(Boolean)
      .join(' | '),
    eventValue: undefined,
  };
};

export const eventOnShowConsentMplScreen = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, searchOutput, `Согласие`].join(' | '),
  };
};

export const eventOnApplyConsentMpl = (data: {
  scenario: string;
  searchOutput: number;
  isWithAdvertising?: boolean;
}): IInitialEvent => {
  const { scenario, searchOutput, isWithAdvertising } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, searchOutput, `Согласие`, isWithAdvertising ? 'С рекламой' : 'Без рекламы'].join(' | '),
  };
};

export const eventOnShowConsentEsiaUpdateBanner = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, searchOutput, 'Обновить согласие Госуслуги'].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnClickUpdateConsentEsia = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Обновить_согласие_госуслуги',
    eventLabel: [scenario, searchOutput, 'Обновить согласие Госуслуги'].join(' | '),
    eventValue: undefined,
  };
};

export const eventOnSubmitCreditPurposeStep = (data: {
  purpose: string;
  amount: string;
  term: string;
}): IInitialEvent => {
  const { purpose, amount, term } = data;
  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [purpose, amount, term].join(' | '),
    eventValue: undefined,
  };
};

const APPROVAL_RANGE_BANNER_LABEL = 'Могут одобрить-баннер';

/** Клик по плашке «Могут одобрить» (ApprovalRangeWidget) */
export const eventOnClickApprovalRangeWidget = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, searchOutput, 'Могут одобрить'].join(' | '),
    eventValue: undefined,
  };
};

/** Показ модалки факторов одобрения («Могут одобрить-баннер») */
export const eventOnShowApprovalRangeBanner = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, searchOutput, APPROVAL_RANGE_BANNER_LABEL].join(' | '),
    eventValue: undefined,
  };
};

/** Закрытие модалки факторов одобрения */
export const eventOnCloseApprovalRangeBanner = (data: { scenario: string; searchOutput: number }): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Выход',
    eventLabel: [scenario, searchOutput, APPROVAL_RANGE_BANNER_LABEL].join(' | '),
    eventValue: undefined,
  };
};

/** Показ суммы в виджете при valueState=available */
export const eventOnShowApprovalRangeBannerAmount = (data: {
  scenario: string;
  searchOutput: number;
  amount: string;
}): IInitialEvent => {
  const { scenario, searchOutput, amount } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, searchOutput, APPROVAL_RANGE_BANNER_LABEL, `Сумма в виджете - ${amount}`].join(' | '),
    eventValue: undefined,
  };
};

/** Показ точки входа в Госуслуги при valueState=esiaAuthRequired */
export const eventOnShowApprovalRangeBannerEsiaEntry = (data: {
  scenario: string;
  searchOutput: number;
}): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, searchOutput, APPROVAL_RANGE_BANNER_LABEL, 'Войти в Госуслуги'].join(' | '),
    eventValue: undefined,
  };
};

/** Клик «Войти в Госуслуги» из модалки факторов одобрения (не общий EsiaAuthBanner) */
export const eventOnClickApprovalRangeBannerEsiaAuth = (data: {
  scenario: string;
  searchOutput: number;
}): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Клик',
    eventLabel: [scenario, searchOutput, APPROVAL_RANGE_BANNER_LABEL, 'Войти в Госуслуги'].join(' | '),
    eventValue: undefined,
  };
};

/** Успешный вход через Госуслуги после возврата с модалки факторов одобрения */
export const eventOnApprovalRangeBannerEsiaAuthSuccess = (data: {
  scenario: string;
  searchOutput: number;
}): IInitialEvent => {
  const { scenario, searchOutput } = data;

  return {
    event: EVENT_TYPE,
    eventCategory: EVENT_CATEGORY,
    eventAction: 'Показ',
    eventLabel: [scenario, searchOutput, APPROVAL_RANGE_BANNER_LABEL, 'Войти в Госуслуги', 'Успешный вход'].join(' | '),
    eventValue: undefined,
  };
};
